import { defineStore } from "pinia";
import { checkoutApi as api } from "./requests";
import { Stripe, loadStripe } from "@stripe/stripe-js";
import { computed, watch, ref } from "vue";
import { debounce } from "lodash";
import { FrequencyKeys, PlanKeys, UpcomingInvoice } from "./types";
import { usePlanStore } from "@/stores/planStore";
import { getEnv } from "@/env";
import { getApiErrorMessage } from "@/api/errors";

const stripeKey = getEnv('VITE_STRIPE_KEY');

export interface BillingAddress {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
}

export const useCheckoutStore = defineStore("cart", () => {
    let stripeClient: Stripe | null = null;
    const cart = ref<any>(null);
    const processing = ref<boolean>(false);

    const planStore = usePlanStore();

    const cardElement = ref<any>(null);
    const stripe = ref<Stripe | null>(null);
    const billingAddress = ref<BillingAddress>({
        line1: "",
        city: "",
        state: "",
        postal_code: "",
        country: "",
    });
    const paymentMethodValid = ref<boolean>(false);
    const paymentMethodError = ref<string>("");
    const customPaymentError = ref<string>("");
    const upcomingInvoice = ref<UpcomingInvoice | null>(null);
    const loadingInvoice = ref<boolean>(false);

    const savedCoupon = (() => {
        try {
            const stored = localStorage.getItem('checkout-coupon');
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    })();

    const couponCode = ref<string>(savedCoupon.code || "");
    const couponId = ref<string | null>(savedCoupon.id || null);
    const discountPercent = ref<number>(savedCoupon.percent_off || 0);
    const discountAmount = ref<number>(savedCoupon.amount_off || 0);

    const alertMessage = ref<{
        type: "error";
        title: string;
        message: string;
        button: string;
    } | null>(null);

    /**
     * Show the checkout alert dialog for a failed request, using the server's user-safe message.
     */
    const showErrorAlert = (title: string, error: unknown) => {
        alertMessage.value = {
            type: "error",
            title,
            message: getApiErrorMessage(error),
            button: "OK",
        };
    };

    const cartSize = computed(() => {
        return cart.value?.items?.length ?? 0;
    });

    const getCheckoutEstimate = debounce(async () => {
        loadingInvoice.value = true;
        if (!planStore.currentPlan && !planStore.currentFrequency) {
            return;
        }

        await api.getCheckoutEstimate(
            billingAddress.value,
            couponId?.value,
            planStore.currentPlan as PlanKeys,
            planStore.currentFrequency as FrequencyKeys
        )
            .then((response) => {
                // null means unauthenticated (HTML redirect) — skip silently
                if (response !== null) {
                    upcomingInvoice.value = response;
                }
                loadingInvoice.value = false;
            })
            .catch((e) => {
                loadingInvoice.value = false;
                alertMessage.value = {
                    type: "error",
                    title: "Error Updating Plan Info",
                    message: e.message,
                    button: "Close",
                };
            });
    }, 500);

    watch(
        [billingAddress, () => planStore.currentPlan, () => planStore.currentFrequency, couponId],
        getCheckoutEstimate,
        { deep: true }
    );

    async function addItemToCart(productSlug: string) {
        await api.setProductInCart(productSlug, 1);
    }

    async function removeItemFromCart(productId: number) {
        await api.removeProduct(productId);
        await getCart();
    }

    async function getCart(cartUuid?: string) {
        cart.value = await api.getCart(cartUuid);
    }

    async function applyCoupon(couponCodeValue: string) {
        try {
            const response = await api.applyCoupon(couponCodeValue);
            if (response.active) {
                couponCode.value = couponCodeValue;
                couponId.value = response.coupon.id;
                discountPercent.value = response.coupon.percent_off;
                discountAmount.value = response.coupon.amount_off;

                const isSpecialOffer = [
                    getEnv('VITE_RENEWAL_OFFER_COUPON_CODE'),
                    getEnv('VITE_RENEWAL_FINAL_OFFER_COUPON_CODE'),
                    getEnv('VITE_CERT_UPSELL_COUPON_CODE'),
                    getEnv('VITE_SAVE_OFFER_COUPON_CODE'),
                ].filter(Boolean).includes(couponCodeValue);

                if (!isSpecialOffer) {
                    localStorage.setItem('checkout-coupon', JSON.stringify({
                        code: couponCodeValue,
                        id: response.coupon.id,
                        percent_off: response.coupon.percent_off,
                        amount_off: response.coupon.amount_off
                    }));
                }

                await getCheckoutEstimate();
            } else {
                alertMessage.value = {
                    type: "error",
                    title: "Invalid Coupon Code",
                    message: "This coupon code is invalid or has expired.",
                    button: "OK",
                };
            }
        } catch (e) {
            alertMessage.value = {
                type: "error",
                title: "Invalid Coupon Code",
                message: "There was an error applying your coupon code. Please try again.",
                button: "OK",
            };
        }
    }

    async function applyCouponFromUrl(): Promise<void> {
        const params = new URLSearchParams(window.location.search);
        const couponCodeFromUrl = params.get("coupon");

        if (couponCodeFromUrl) {
            await applyCoupon(couponCodeFromUrl);
        }
    }

    function removeCoupon() {
        couponCode.value = "";
        couponId.value = null;
        discountPercent.value = 0;
        discountAmount.value = 0;

        localStorage.removeItem('checkout-coupon');

        getCheckoutEstimate();
    }

    const billingAddressIsValid = () => {
        // if country is US validate full address
        if (
            billingAddress.value.country === "US" ||
            billingAddress.value.country === "CA"
        ) {
            return (
                billingAddress.value.line1.length > 0 &&
                billingAddress.value.city.length > 0 &&
                billingAddress.value.state.length > 0 &&
                billingAddress.value.postal_code.length > 0 &&
                billingAddress.value.country.length > 0
            );
        }
        // if country is not US validate only country
        return billingAddress.value.country.length > 0;
    };

    const cartValid = computed(() => {
        return (
            (upcomingInvoice.value?.total ?? 0) >= 0 &&
            billingAddressIsValid() &&
            paymentMethodValid.value
        );
    });

    const allItemsAreValid = () => {
        return cart.value?.items?.every((item: any) => item.valid);
    };

    return {
        planStore,
        addItemToCart,
        applyCoupon,
        applyCouponFromUrl,
        removeCoupon,
        billingAddress,
        cart,
        cardElement,
        discountAmount,
        discountPercent,
        cartSize,
        couponCode,
        couponId,
        upcomingInvoice,
        loadingInvoice,
        paymentMethodValid,
        paymentMethodError,
        customPaymentError,
        processing,
        billingAddressIsValid,
        stripe,
        cartValid,
        alertMessage,
        showErrorAlert,
        getCart,
        removeItemFromCart,
        getCheckoutEstimate,
        getStripeClient: async () => {
            if (stripeClient) {
                return stripeClient;
            }
            stripeClient = await loadStripe(stripeKey);
            stripe.value = stripeClient;
            return stripeClient;
        },
        setCustomPaymentError: (error: string) => {
            customPaymentError.value = error;
        },
        clearCustomPaymentError: () => {
            customPaymentError.value = "";
        },
        checkout: async (): Promise<boolean> => {
            // Clear any previous custom errors
            customPaymentError.value = "";

            // Check if credit card form is empty (not just invalid)
            if (!paymentMethodValid.value && !paymentMethodError.value) {
                customPaymentError.value = "Please enter your credit card information to continue.";
                return false;
            }

            // Check other validation requirements
            if (!cartValid.value) {
                // If billing address is invalid, show appropriate error
                if (!billingAddressIsValid()) {
                    customPaymentError.value = "Please complete your billing address.";
                } else if (!paymentMethodValid.value) {
                    customPaymentError.value = "Please enter valid credit card information.";
                }
                return false;
            }

            processing.value = true;
            try {
                const result = await stripe.value?.createPaymentMethod({
                    type: "card",
                    card: cardElement.value,
                });
                if (result?.error) {
                    throw new Error(result.error.message);
                }
                if (result?.paymentMethod) {
                    const paymentMethodId = result?.paymentMethod.id;
                    const response = await api.checkout(
                        paymentMethodId,
                        billingAddress.value,
                        planStore.currentPlan as PlanKeys,
                        planStore.currentFrequency as FrequencyKeys,
                        couponId.value
                    );

                    // Handle error responses from the controller
                    if (response.code >= 400) {
                        const errorData = response.data;

                        alertMessage.value = {
                            type: "error",
                            title: "Payment Error",
                            message: errorData?.message || "We had problems processing your payment. Please try again.",
                            button: "OK",
                        };
                        processing.value = false;
                        return false;
                    }

                    couponCode.value = "";
                    couponId.value = null;
                    discountPercent.value = 0;
                    discountAmount.value = 0;
                    localStorage.removeItem('checkout-coupon');
                }
                return true;
            } catch (e: any) {
                alertMessage.value = {
                    type: "error",
                    title: "Checkout Error",
                    message: e.response?.data?.message || e.message || "We had problems processing your payment. Please try again.",
                    button: "OK",
                };
                processing.value = false;
                return false;
            }
        },
        async getBillingInfo() {
            const billingInfo = await api.getBillingInfo();
            billingAddress.value = billingInfo;
        },
        async logCheckoutStarted() {
            await api.logCheckoutStarted();
        },
    };
});
